import { Component, OnInit, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';

import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatCardModule } from '@angular/material/card';
import { MatTableModule } from '@angular/material/table';

import { ApiService } from '../../core/services/api.service';
import { QaApiService } from './qa.service';
import { TestPlanDto } from './qa.models';

interface ProjectRow { id: string; name: string; }

@Component({
  selector: 'app-qa',
  standalone: true,
  imports: [
    CommonModule, FormsModule,
    MatFormFieldModule, MatSelectModule, MatInputModule, MatButtonModule,
    MatIconModule, MatProgressSpinnerModule, MatCardModule, MatTableModule
  ],
  templateUrl: './qa.component.html',
  styleUrl: './qa.component.scss'
})
export class QaComponent implements OnInit {
  private readonly api = inject(ApiService);
  private readonly qa = inject(QaApiService);
  private readonly router = inject(Router);

  readonly loading = signal(false);
  readonly saving = signal(false);
  readonly error = signal<string | null>(null);
  readonly projects = signal<ProjectRow[]>([]);
  readonly plans = signal<TestPlanDto[]>([]);

  filterProjectId = '';
  showCreate = false;
  planName = '';
  planDescription = '';
  planVersion = '';
  planProjectId = '';

  readonly columns = ['name', 'version', 'isActive', 'actions'];

  ngOnInit(): void {
    this.api.get<{ items?: ProjectRow[] } | ProjectRow[]>('v1/projects', { take: 100 }).subscribe({
      next: (res) => {
        const items = Array.isArray(res) ? res : (res.items ?? []);
        this.projects.set(items);
        if (items.length) {
          this.filterProjectId = items[0].id;
          this.planProjectId = items[0].id;
        }
        this.loadPlans();
      },
      error: (err) => this.error.set(err?.message || 'Failed to load projects.')
    });
  }

  loadPlans(): void {
    this.loading.set(true);
    this.qa.list({ projectId: this.filterProjectId || undefined, pageSize: 100 }).subscribe({
      next: (page) => {
        this.plans.set(page.items ?? []);
        this.loading.set(false);
      },
      error: (err) => {
        this.error.set(err?.message || 'Failed to load test plans.');
        this.plans.set([]);
        this.loading.set(false);
      }
    });
  }

  onFilterChange(): void {
    this.loadPlans();
  }

  createPlan(): void {
    if (!this.planProjectId || !this.planName.trim()) {
      this.error.set('Project and plan name are required.');
      return;
    }
    this.saving.set(true);
    this.qa.create({
      projectId: this.planProjectId,
      name: this.planName.trim(),
      description: this.planDescription.trim() || undefined,
      version: this.planVersion.trim() || undefined,
      isActive: true
    }).subscribe({
      next: (plan) => {
        this.saving.set(false);
        this.showCreate = false;
        this.planName = '';
        this.planDescription = '';
        this.planVersion = '';
        this.loadPlans();
        this.router.navigate(['/qa', plan.id]);
      },
      error: (err) => {
        this.error.set(err?.message || 'Failed to create test plan.');
        this.saving.set(false);
      }
    });
  }

  openPlan(id: string): void {
    this.router.navigate(['/qa', id]);
  }

  deletePlan(plan: TestPlanDto): void {
    if (!confirm(`Delete test plan "${plan.name}"?`)) return;
    this.qa.delete(plan.id).subscribe({
      next: () => this.loadPlans(),
      error: (err) => this.error.set(err?.message || 'Failed to delete test plan.')
    });
  }
}
